<template>
    <div class="customer-default-speakers-modal">
        <slot name="trigger" :open="open">
            <button
                type="button"
                class="button button--big customer-default-speakers-modal__trigger"
                @click="open"
            >
                <IconPlus class="icon" />
                <span v-t="'crm.defaultSpeakers.create.trigger'" />
            </button>
        </slot>
        <portal to="modals">
            <Modal
                v-if="opened"
                name="default-speakers"
                modal-class="customer-default-speakers-modal__modal"
                :title="modalTitle"
                @close="close"
            >
                <template #content>
                    <Spinner v-if="loading" />
                    <form
                        id="customer-default-speakers-modal"
                        class="customer-default-speakers-modal__form"
                        @submit.prevent="submit"
                    >
                        <CustomerDefaultSpeakersForm
                            v-model="values"
                            :schema="schema"
                            :book-languages="bookLanguages"
                            :can-delete="false"
                        />
                    </form>
                    <div
                        v-if="error"
                        class="customer-default-speakers-modal__error"
                        v-text="error"
                    />
                </template>
                <template #bottom>
                    <button
                        type="button"
                        :disabled="loading"
                        :class="['button button--big button--outlined', { 'is-disabled': loading }]"
                        @click="close"
                        v-text="$t('misc.cancel')"
                    />
                    <button
                        type="submit"
                        form="customer-default-speakers-modal"
                        :disabled="!canSave"
                        :class="['button button--big', { 'is-disabled': !canSave }]"
                        v-text="$t('misc.save')"
                    />
                </template>
            </Modal>
        </portal>
    </div>
</template>

<script>
import { createCustomersDefaultSpeaker, updateCustomersDefaultSpeaker } from '../../api/customers';
import CustomerDefaultSpeakersForm from '../CustomerDefaultSpeakersForm/CustomerDefaultSpeakersForm';
import { Modal } from '@speechki-frontend/shared/ui/modals';
import { IconPlus } from '@speechki-frontend/shared/ui/icons';

export default {
    name: 'CustomerDefaultSpeakersModal',
    components: {
        Modal,
        IconPlus,
        CustomerDefaultSpeakersForm,
    },
    props: {
        schema: {
            type: Object,
            required: true,
        },
        bookLanguages: {
            type: Array,
            default: () => [],
        },
        customer: {
            type: [Number, String],
            required: true,
        },
        item: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            loading: false,
            opened: false,
            error: undefined,
            initialValues: {
                customer: this.customer,
                language: undefined,
                speaker: undefined,
            },
            values: undefined,
        };
    },
    computed: {
        modalTitle() {
            return this.$t(`crm.defaultSpeakers.${'id' in this.item ? 'update' : 'create'}.title`);
        },
        isValid() {
            if (!this.values) return false;
            return Object.keys(this.schema)
                .filter((k) => this.schema[k].required)
                .every((k) => !!this.values[k]);
        },
        canSave() {
            return !this.loading && this.isValid;
        },
    },
    watch: {
        opened(value) {
            this.values = value ? { ...this.initialValues, ...this.item } : undefined;
            this.error = undefined;
            this.$emit(value ? 'opened' : 'closed');
        },
    },
    methods: {
        open() {
            this.opened = true;
        },
        close() {
            this.opened = false;
        },
        async submit() {
            this.loading = true;

            try {
                const result = await this.save();

                this.$emit('saved', result);
                this.close();
            } catch (err) {
                this.error = (err.non_field_errors || [])[0];
                console.log(err);
            } finally {
                this.loading = false;
            }
        },
        save() {
            if (this.item.id) return updateCustomersDefaultSpeaker(this.item.id, this.values);
            return createCustomersDefaultSpeaker(this.values);
        },
    },
};
</script>

<style lang="scss" src="./CustomerDefaultSpeakersModal.scss"></style>
